import { createFileRoute } from "@tanstack/react-router";
import {
	Bot,
	Building2,
	GitBranch,
	ShieldCheck,
	Sparkles,
	Users,
} from "lucide-react";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/for/agencies";

export const Route = createFileRoute(path)({
	component: ForAgencies,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher for Agencies - AI Social Media at Scale",
			description:
				"Run client social channels with one workspace per brand, dedicated agents, approvals, and connected accounts that scale up to 150.",
		}),
});

function ForAgencies() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="For agencies"
				title="Run more clients without hiring more people."
				description="Every client gets their own workspace, agent, and approval flow. Brand voices stay distinct, reviews stay fast, and margins stay yours."
			/>

			<MarketingSection
				title="What agencies get out of AgentReacher"
				description="Built for shops that manage 5 to 50 client accounts."
			>
				<FeatureGrid
					features={[
						{
							icon: Building2,
							title: "Workspaces per brand",
							description:
								"Each client has its own workspace, agent, calendar, and connected accounts. No mixing.",
						},
						{
							icon: Bot,
							title: "Voice per workspace",
							description:
								"Agents are trained per brand. The voice stays consistent across the team that operates it.",
						},
						{
							icon: ShieldCheck,
							title: "Approval gates",
							description:
								"Client review before publishing, with reviewer roles built into every plan.",
						},
						{
							icon: Users,
							title: "Team seats",
							description:
								"Up to 30 team members on Max. Roles for strategists, writers, and reviewers.",
						},
						{
							icon: GitBranch,
							title: "150 connected accounts",
							description:
								"Scales to large rosters without per-account upcharges.",
						},
						{
							icon: Sparkles,
							title: "Multi-brand reporting",
							description:
								"See cadence, performance, and queue health across every brand at once.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection title="How agencies set this up" align="left">
				<ol className="mx-auto max-w-2xl space-y-3 text-sm leading-relaxed text-muted-foreground">
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							1
						</span>
						<span>Create a workspace per client. Connect their accounts.</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							2
						</span>
						<span>
							Train each agent on the client's brand voice using past posts.
						</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							3
						</span>
						<span>
							Invite strategists and reviewers. Set approval gates per client.
						</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							4
						</span>
						<span>
							Operate every client from chat. Switch workspaces as you switch
							context.
						</span>
					</li>
				</ol>
			</MarketingSection>

			<MarketingSection>
				<div className="mx-auto max-w-2xl space-y-4 text-center text-sm leading-relaxed text-muted-foreground">
					<p>
						Max plans cover most agencies. Reach out for custom enterprise
						setups across 50+ clients.
					</p>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	);
}
